import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../../store';
import { SystemUserTypes } from '../../../types';
import { MobileAppUserService } from '../../../services/mobile-app-user.service';
import { MobileAppUserTypes } from '../../../types/mobile-app-user.types';
import { UnifiedFilterBar, FilterField } from '../../shared/UnifiedFilterBar';
import { displayName } from '../../../helpers/displayName';

interface Props {
    onFilter(filters: SystemUserTypes.Filters): Promise<void>;
    onDownload(): Promise<void>;
}

type FilterValues = {
    fullname: string;
    email: string;
    teams: string;
    customerId: string;
};

const EMPTY: FilterValues = { fullname: '', email: '', teams: '', customerId: '' };

const primaryFilters: FilterField[] = [
    { key: 'fullname', label: 'User Name', type: 'text', placeholder: 'Search by user name...' },
    { key: 'email', label: 'Email', type: 'text', placeholder: 'Search by email...' },
    { key: 'teams', label: 'Teams', type: 'text', placeholder: 'Filter by team name...' },
];

// Keys that only affect the client-side post-filter (see applyFilters in
// system-user-list/index.tsx). Not sent to the backend.
const CLIENT_ONLY_KEYS = new Set<keyof FilterValues>(['teams']);

export const SystemUserListFilters: React.FC<Props> = ({ onFilter, onDownload }) => {
    const { authUser } = useSelector((state: RootState) => state.auth);
    const showCustomerFilter = Boolean(authUser?.pomMaster && !authUser?.masquarade);

    const [customers, setCustomers] = useState<MobileAppUserTypes.CustomerForFilter[]>([]);

    // `values` = what's currently in the inputs; `applied` = the snapshot of
    // the last set we sent to onFilter(). Pills render from `applied` so they
    // don't flicker as the user types a new value before hitting Apply.
    const [values, setValues] = useState<FilterValues>(EMPTY);
    const [applied, setApplied] = useState<FilterValues>(EMPTY);

    useEffect(() => {
        let isMounted = true;
        if (!showCustomerFilter) return;

        MobileAppUserService.getCustomersForFilter()
            .then((data) => { if (isMounted) setCustomers(data); })
            .catch((err) => console.error('[SystemUserListFilters] customer load failed', err));

        return () => { isMounted = false; };
    }, [showCustomerFilter]);

    const expandedFilters: FilterField[] = useMemo(() => {
        if (!showCustomerFilter) return [];
        return [{
            key: 'customerId',
            label: 'Customer',
            type: 'select',
            options: [
                { label: 'Any', value: '' },
                ...customers.map((c) => ({ label: c.name, value: c._id })),
            ],
        }];
    }, [showCustomerFilter, customers]);

    // Backend payload: omit keys that are empty strings, mirroring the old
    // normalizeFilters behavior. Leaving them in as '' can produce a stricter
    // "equals empty" query depending on how the backend query-builder handles
    // empty-string vs absent params. Teams is client-side-only but kept on
    // the object so index.tsx can post-filter.
    const toApiFilters = (v: FilterValues): SystemUserTypes.Filters => {
        const out: SystemUserTypes.Filters = {} as SystemUserTypes.Filters;
        if (v.fullname) out.fullname = v.fullname;
        if (v.email) out.email = v.email;
        if (v.teams) out.teams = v.teams;
        if (v.customerId) out.customerId = v.customerId;
        return out;
    };

    const handleChange = useCallback((key: string, value: any) => {
        setValues((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleApply = useCallback(() => {
        setApplied(values);
        onFilter(toApiFilters(values)).catch((err) => {
            console.error('[SystemUserListFilters] onFilter failed', err);
        });
    }, [values, onFilter]);

    const handleClear = useCallback(() => {
        setValues(EMPTY);
        setApplied(EMPTY);
        onFilter(toApiFilters(EMPTY)).catch((err) => {
            console.error('[SystemUserListFilters] onFilter (clear) failed', err);
        });
    }, [onFilter]);

    // Remove a single applied filter — clear that key in both values + applied
    // and immediately re-apply so the list updates without a manual click.
    const removeApplied = useCallback((key: keyof FilterValues) => {
        const nextValues = { ...values, [key]: '' };
        const nextApplied = { ...applied, [key]: '' };
        setValues(nextValues);
        setApplied(nextApplied);
        onFilter(toApiFilters(nextApplied)).catch((err) => {
            console.error('[SystemUserListFilters] onFilter (remove pill) failed', err);
        });
    }, [values, applied, onFilter]);

    // Label resolvers — convert stored ids to human text for the pill.
    const customerNameById = useMemo(() => {
        const m = new Map<string, string>();
        for (const c of customers) m.set(c._id, c.name);
        return m;
    }, [customers]);

    const appliedFilters = useMemo(() => {
        const pills: { key: string; label: string; onRemove: () => void }[] = [];
        if (applied.fullname) pills.push({ key: 'fullname', label: `Name: ${applied.fullname}`, onRemove: () => removeApplied('fullname') });
        if (applied.email) pills.push({ key: 'email', label: `Email: ${applied.email}`, onRemove: () => removeApplied('email') });
        if (applied.teams) pills.push({ key: 'teams', label: `Team: ${applied.teams}`, onRemove: () => removeApplied('teams') });
        if (applied.customerId) {
            const name = displayName(customerNameById.get(applied.customerId));
            pills.push({ key: 'customerId', label: `Customer: ${name}`, onRemove: () => removeApplied('customerId') });
        }
        return pills;
    }, [applied, customerNameById, removeApplied]);

    // Suppress unused-var lint on CLIENT_ONLY_KEYS — it's documentation for
    // which keys the backend does not see. Keeping it referenced here so
    // future changes can't silently drift from the contract.
    void CLIENT_ONLY_KEYS;

    return (
        <UnifiedFilterBar
            primaryFilters={primaryFilters}
            expandedFilters={expandedFilters}
            values={values}
            appliedFilters={appliedFilters}
            onChange={handleChange}
            onApply={handleApply}
            onClear={handleClear}
            onExport={onDownload}
        />
    );
};
